import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { WxrlRuleTrigger, WrxlRulesService } from '../../services/wxrl-rules.service';

@Component({
  selector: 'afoone-wxrl-trigger-drawer',
  templateUrl: './wxrl-trigger-drawer.component.html',
  styleUrls: ['./wxrl-trigger-drawer.component.css'],
})
export class WxrlTriggerDrawerComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
  @Input() visible: boolean = false;
  @Input() trigger: WxrlRuleTrigger | null = null;
  @Input() isEditMode: boolean = false;
  @Input() labNr?: number;
  @Input() labType?: string;
  @Input() isNewRule: boolean = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<WxrlRuleTrigger>();
  @Output() triggerCancel = new EventEmitter<void>();

  public triggerForm: FormGroup;

  // Opciones de desplegables
  public triggerTypes: { value: string; label: string }[] = [];
  public triggerStates: { value: string; label: string }[] = [];
  public triggerTests: { value: number; label: string }[] = [];
  public triggerEvents: { value: string; label: string }[] = [];
  public alarmTypes: { value: string; label: string }[] = [];
  public alarmActions: { value: string; label: string }[] = [];
  public textResults: { value: number; label: string }[] = [];
  public isolationOptions: { value: string; label: string }[] = [];
  public debugOptions: { value: number; label: string }[] = [
    { value: 0, label: '0' },
    { value: 1, label: '1' },
    { value: 2, label: '2' }
  ];

  private destroyed$ = new Subject<void>();

  constructor(
    private _fb: FormBuilder,
    private _translate: TranslateService,
    private _wxrlService: WrxlRulesService,
    private _cdr: ChangeDetectorRef
  ) {
    this.triggerForm = this._fb.group({
      subjectType: ['', Validators.required],
      subjectID: [null],
      subjectName: [''],
      methNrFilter: [''],
      alarmFilter: [''],
      // En alta, PosR lo asigna el servidor (#559: MAX+1 por MethNr en RULESMETHODE). El 1 satisface el validador hasta guardar.
      posR: [1, [Validators.required, Validators.min(1)]],
      ruleState: ['', Validators.required],
      triggEvent: ['', Validators.required],
      auftrNrTest: [''],
      debug: [0, Validators.required],
      kommentar: ['', [Validators.maxLength(255)]],
      // Campos temporales para UI
      textNr: [null],
      alarmStatus: [''],
      isolation: ['']
    });
  }

  ngOnInit(): void {
    this.loadStaticOptions();

    // Listener para cambios en subjectType
    this.triggerForm.get('subjectType')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newType) => {
        // Limpiar campos que no corresponden al nuevo tipo
        if (newType !== 'RXTX' && newType !== 'Texto') {
          this.triggerForm.patchValue({ textNr: null }, { emitEvent: false });
        }
        if (newType !== 'RXAL' && newType !== 'Alarma') {
          this.triggerForm.patchValue({
            alarmStatus: '',
            alarmFilter: ''
          }, { emitEvent: false });
        }
        if (newType !== 'RXPR' && newType !== 'Prueba') {
          // No limpiar methNrFilter porque es visible para todos los tipos
        }

        this.updateValidators();
        this.loadDynamicOptions();
        this.preselectEvent();
      });

    // Listener para cambios en methNrFilter (Prueba)
    this.triggerForm.get('methNrFilter')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value && this.triggerTests.length > 0) {
          const selectedTest = this.triggerTests.find(t => t.value === value);
          if (selectedTest) {
            const subjectType = this.triggerForm.get('subjectType')?.value;
            if (subjectType === 'RXPR' || subjectType === 'Prueba') {
              this.triggerForm.patchValue({
                subjectID: value,
                subjectName: selectedTest.label
              }, { emitEvent: false });
            }
          }
        }
      });

    // Listener para cambios en textNr (Texto)
    this.triggerForm.get('textNr')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value && this.textResults.length > 0) {
          const selectedText = this.textResults.find(t => t.value === value);
          if (selectedText) {
            this.triggerForm.patchValue({
              subjectID: value,
              subjectName: selectedText.label.replace(/^\(\d+\)\s*/, '') // Remover formato (TextNr)
            }, { emitEvent: false });
          }
        }
      });

    // Listener para cambios en alarmStatus (Tipo de Alarma)
    this.triggerForm.get('alarmStatus')?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value && this.alarmTypes.length > 0) {
          const selectedAlarm = this.alarmTypes.find(a => a.value === value);
          if (selectedAlarm) {
            this.triggerForm.patchValue({
              subjectID: value,
              subjectName: selectedAlarm.label
            }, { emitEvent: false });
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    // Envuelve TODA la detección de cambios en un setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this._cdr.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      if (this.trigger && this.isEditMode) {
        // Primero establecer el subjectType en el formulario para que loadDynamicOptions pueda usarlo
        const normalizedSubjectType = this.normalizeSubjectType(this.trigger.subjectType);
        this.triggerForm.patchValue({ subjectType: normalizedSubjectType }, { emitEvent: false });

        // Cargar opciones dinámicas basadas en el tipo, luego cargar el resto del trigger
        this.loadDynamicOptions().then(() => {
          this.loadTriggerFromData(this.trigger);
        });
      } else {
        this.initializeForCreation();
        this.loadDynamicOptions();
      }
    } else if (changes['visible'] && !this.visible) {
      this.triggerForm.reset({
        posR: 1,
        debug: 0
      });
    }
  }

  private normalizeSubjectType(subjectType: string): string {
    if (subjectType === 'Prueba') {
      return 'RXPR';
    } else if (subjectType === 'Texto') {
      return 'RXTX';
    } else if (subjectType === 'Alarma') {
      return 'RXAL';
    }
    return subjectType;
  }

  private async loadStaticOptions(): Promise<void> {
    try {
      this.triggerTypes = await this._wxrlService.getTriggerTypes();
      this.triggerStates = await this._wxrlService.getTriggerStates();
      if (this.labNr) {
        this.triggerTests = await this._wxrlService.getTriggerTests(this.labNr);
        this.textResults = await this._wxrlService.getTextResults(this.labNr);
      }
      if (this.labType === 'LABM') {
        this.isolationOptions = await this._wxrlService.getIsolationOptions();
      }
    } catch (error) {
      console.error('Error loading static options:', error);
    }
  }

  private async loadDynamicOptions(): Promise<void> {
    const subjectType = this.triggerForm.get('subjectType')?.value;
    if (!subjectType) {
      this.triggerEvents = [];
      return;
    }

    try {
      // Cargar eventos según tipo
      this.triggerEvents = await this._wxrlService.getTriggerEvents(subjectType);

      // Cargar opciones específicas según tipo
      if (subjectType === 'RXAL' || subjectType === 'Alarma') {
        this.alarmTypes = await this._wxrlService.getAlarmTypes();
        this.alarmActions = await this._wxrlService.getAlarmActions();
      } else {
        this.alarmTypes = [];
        this.alarmActions = [];
      }

      if (subjectType === 'RXTX' || subjectType === 'Texto') {
        if (this.labNr) {
          this.textResults = await this._wxrlService.getTextResults(this.labNr);
        }
      }
    } catch (error) {
      console.error('Error loading dynamic options:', error);
    }
  }

  private updateNamesFromOptions(): void {
    const subjectType = this.triggerForm.get('subjectType')?.value;
    const subjectID = this.triggerForm.get('subjectID')?.value;

    if (!subjectID) return;

    if (subjectType === 'RXPR' || subjectType === 'Prueba') {
      this.updateNameFromSelectedTest();
      return;
    }

    if (subjectType === 'RXTX' || subjectType === 'Texto') {
      this.updateNameFromSelectedText();
      return;
    }

    if (subjectType === 'RXAL' || subjectType === 'Alarma') {
      this.updateNameFromSelectedAlarm();
    }
  }

  private updateNameFromSelectedTest(): void {
    const methNrFilter = this.triggerForm.get('methNrFilter')?.value;
    const selectedTest = this.triggerTests.find(t => t.value === methNrFilter);
    if (selectedTest) {
      this.triggerForm.patchValue({ subjectName: selectedTest.label }, { emitEvent: false });
    }
  }

  private updateNameFromSelectedText(): void {
    const textNr = this.triggerForm.get('textNr')?.value;
    const selectedText = this.textResults.find(t => t.value === textNr);
    if (selectedText) {
      this.triggerForm.patchValue({
        subjectName: selectedText.label.replace(/^\(\d+\)\s*/, '')
      }, { emitEvent: false });
    }
  }

  private updateNameFromSelectedAlarm(): void {
    const alarmStatus = this.triggerForm.get('alarmStatus')?.value;
    const selectedAlarm = this.alarmTypes.find(a => a.value === alarmStatus);
    if (selectedAlarm) {
      this.triggerForm.patchValue({ subjectName: selectedAlarm.label }, { emitEvent: false });
    }
  }

  private toDropdownNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private preselectEvent(): void {
    const subjectType = this.triggerForm.get('subjectType')?.value;
    if (!this.isEditMode) {
      if (subjectType === 'RXAL' || subjectType === 'Alarma') {
        const calaEvent = this.triggerEvents.find(e => e.value === 'CALA');
        if (calaEvent) {
          this.triggerForm.patchValue({ triggEvent: 'CALA' });
        }
      } else if (subjectType === 'RXTX' || subjectType === 'Texto') {
        const ctxtEvent = this.triggerEvents.find(e => e.value === 'CTXT');
        if (ctxtEvent) {
          this.triggerForm.patchValue({ triggEvent: 'CTXT' });
        }
      }
    }
  }

  private updateValidators(): void {
    const subjectType = this.triggerForm.get('subjectType')?.value;

    // Remover validadores de campos condicionales
    this.triggerForm.get('textNr')?.clearValidators();
    this.triggerForm.get('alarmStatus')?.clearValidators();
    this.triggerForm.get('methNrFilter')?.clearValidators();
    this.triggerForm.get('alarmFilter')?.clearValidators();

    // Agregar validadores según tipo
    if (subjectType === 'RXTX' || subjectType === 'Texto') {
      this.triggerForm.get('textNr')?.setValidators([Validators.required]);
    }
    if (subjectType === 'RXAL' || subjectType === 'Alarma') {
      this.triggerForm.get('alarmStatus')?.setValidators([Validators.required]);
      this.triggerForm.get('alarmFilter')?.setValidators([Validators.required]);
    }
    if (subjectType === 'RXPR' || subjectType === 'Prueba') {
      this.triggerForm.get('methNrFilter')?.setValidators([Validators.required]);
    }

    // Actualizar estado de validación
    this.triggerForm.get('textNr')?.updateValueAndValidity();
    this.triggerForm.get('alarmStatus')?.updateValueAndValidity();
    this.triggerForm.get('methNrFilter')?.updateValueAndValidity();
    this.triggerForm.get('alarmFilter')?.updateValueAndValidity();
  }

  private loadTriggerFromData(trigger: WxrlRuleTrigger): void {
    // Normalizar subjectType: si viene como descripción, convertir a código
    const subjectType = this.normalizeSubjectType(trigger.subjectType);

    // Normalizar auftrNrTest: mantener el valor tal cual viene del backend
    // Si es null/undefined, usar string vacío; si es solo espacios, mantenerlo (puede ser válido)
    const auftrNrTestValue = trigger.auftrNrTest ?? '';

    // Mapear campos según tipo (sin kommentar primero para evitar ExpressionChangedAfterItHasBeenCheckedError)
    if (subjectType === 'RXPR') {
      const subjectID = this.toDropdownNumber(trigger.subjectID);

      this.triggerForm.patchValue({
        subjectType: subjectType,
        subjectID,
        subjectName: trigger.subjectName,
        methNrFilter: subjectID, // Para RXPR, methNrFilter = subjectID
        posR: trigger.posR,
        ruleState: trigger.ruleState,
        triggEvent: trigger.triggEvent,
        auftrNrTest: auftrNrTestValue,
        debug: trigger.debug
      }, { emitEvent: false });
    } else if (subjectType === 'RXTX') {
      this.triggerForm.patchValue({
        subjectType: subjectType,
        subjectID: trigger.subjectID,
        subjectName: trigger.subjectName,
        textNr: trigger.subjectID, // Para RXTX, textNr = subjectID
        methNrFilter: trigger.methNrFilter,
        posR: trigger.posR,
        ruleState: trigger.ruleState,
        triggEvent: trigger.triggEvent,
        auftrNrTest: auftrNrTestValue,
        debug: trigger.debug
      }, { emitEvent: false });
    } else if (subjectType === 'RXAL') {
      this.triggerForm.patchValue({
        subjectType: subjectType,
        subjectID: trigger.subjectID,
        subjectName: trigger.subjectName,
        alarmStatus: trigger.subjectID, // Para RXAL, alarmStatus = subjectID
        methNrFilter: trigger.methNrFilter,
        alarmFilter: trigger.alarmFilter,
        posR: trigger.posR,
        ruleState: trigger.ruleState,
        triggEvent: trigger.triggEvent,
        auftrNrTest: auftrNrTestValue,
        debug: trigger.debug
      }, { emitEvent: false });
    }

    // Establecer kommentar después de un delay para evitar ExpressionChangedAfterItHasBeenCheckedError con PrimeNG textarea
    setTimeout(() => {
      this.triggerForm.patchValue({
        kommentar: trigger.kommentar || ''
      }, { emitEvent: false });
      this._cdr.detectChanges();
    }, 200);

    // Actualizar nombres desde opciones si están disponibles
    setTimeout(() => {
      this.updateNamesFromOptions();
      this.updateValidators();
    }, 100);
  }

  private initializeForCreation(): void {
    // posR por defecto: el API ignora este valor en creación y calcula el siguiente PosR global por prueba (#559).
    // ruleState por defecto: Inactivo (RXIN) según #558.
    this.triggerForm.patchValue({
      posR: 1,
      debug: 0,
      ruleState: 'RXIN'
    });
  }

  private buildTriggerData(): WxrlRuleTrigger {
    const formValue = this.triggerForm.value;
    const subjectType = formValue.subjectType;

    // Normalizar subjectType: asegurar que sea código
    let normalizedSubjectType = subjectType;
    if (subjectType === 'Prueba') {
      normalizedSubjectType = 'RXPR';
    } else if (subjectType === 'Texto') {
      normalizedSubjectType = 'RXTX';
    } else if (subjectType === 'Alarma') {
      normalizedSubjectType = 'RXAL';
    }

    // Obtener la descripción del tipo desde las opciones
    const typeOption = this.triggerTypes.find(
      type => type.value === normalizedSubjectType
    );
    const subjectTypeDescription = typeOption ? typeOption.label : null;

    // Obtener la descripción del estado desde las opciones
    const stateOption = this.triggerStates.find(
      state => state.value === formValue.ruleState
    );
    const ruleStateDescription = stateOption ? stateOption.label : null;

      // Obtener la descripción del evento desde las opciones
      const eventOption = this.triggerEvents.find(
        event => event.value === formValue.triggEvent
      );
      const triggEventDescription = eventOption ? eventOption.label : null;

      // Mapear campos temporales de vuelta a estructura original
      let triggerData: WxrlRuleTrigger;

      if (normalizedSubjectType === 'RXPR') {
        triggerData = {
          subjectType: normalizedSubjectType,
          subjectTypeDescription: subjectTypeDescription || undefined,
          subjectID: formValue.methNrFilter || formValue.subjectID,
          subjectName: formValue.subjectName || '',
          methNrFilter: 'N/A',
          alarmFilter: 'N/A',
          posR: formValue.posR,
          ruleState: formValue.ruleState,
          ruleStateDescription: ruleStateDescription || undefined,
          triggEvent: formValue.triggEvent,
          triggEventDescription: triggEventDescription || undefined,
          auftrNrTest: formValue.auftrNrTest || '',
          debug: formValue.debug,
          kommentar: formValue.kommentar || ''
        };
      } else if (normalizedSubjectType === 'RXTX') {
        triggerData = {
          subjectType: normalizedSubjectType,
          subjectTypeDescription: subjectTypeDescription || undefined,
          subjectID: formValue.textNr || formValue.subjectID,
          subjectName: formValue.subjectName || '',
          methNrFilter: formValue.methNrFilter || '',
          alarmFilter: 'N/A',
          posR: formValue.posR,
          ruleState: formValue.ruleState,
          ruleStateDescription: ruleStateDescription || undefined,
          triggEvent: formValue.triggEvent,
          triggEventDescription: triggEventDescription || undefined,
          auftrNrTest: formValue.auftrNrTest || '',
          debug: formValue.debug,
          kommentar: formValue.kommentar || ''
        };
      } else if (normalizedSubjectType === 'RXAL') {
        triggerData = {
          subjectType: normalizedSubjectType,
          subjectTypeDescription: subjectTypeDescription || undefined,
          subjectID: formValue.alarmStatus || formValue.subjectID,
          subjectName: formValue.subjectName || '',
          methNrFilter: formValue.methNrFilter || '',
          alarmFilter: formValue.alarmFilter || '',
          posR: formValue.posR,
          ruleState: formValue.ruleState,
          ruleStateDescription: ruleStateDescription || undefined,
          triggEvent: formValue.triggEvent,
          triggEventDescription: triggEventDescription || undefined,
          auftrNrTest: formValue.auftrNrTest || '',
          debug: formValue.debug,
          kommentar: formValue.kommentar || ''
        };
      } else {
        triggerData = {
          ...formValue as WxrlRuleTrigger,
          subjectType: normalizedSubjectType,
          subjectTypeDescription: subjectTypeDescription || undefined,
          ruleStateDescription: ruleStateDescription || undefined,
          triggEventDescription: triggEventDescription || undefined
        };
      }

      return triggerData;
  }

  onSave(): void {
    if (this.triggerForm.valid) {
      const triggerData = this.buildTriggerData();
      if (triggerData) {
        this.save.emit(triggerData);
      }
    } else {
      Object.keys(this.triggerForm.controls).forEach((key) => {
        this.triggerForm.get(key)?.markAsTouched();
      });
    }
  }

  onCancel(): void {
    this.visibleChange.emit(false);
    this.triggerCancel.emit();
  }

  onVisibleChange(value: boolean): void {
    this.visibleChange.emit(value);
  }

  get drawerTitle(): string {
    return this._translate.instant('WXRL.FORM_HEADER.TITLE_DISPARADORES');
  }

  // Getters para visibilidad condicional
  get showTextField(): boolean {
    const subjectType = this.triggerForm.get('subjectType')?.value;
    return subjectType === 'RXTX' || subjectType === 'Texto';
  }

  get showAlarmFields(): boolean {
    const subjectType = this.triggerForm.get('subjectType')?.value;
    return subjectType === 'RXAL' || subjectType === 'Alarma';
  }

  get showIsolationField(): boolean {
    return this.labType === 'LABM';
  }
}
